import React, { useMemo, useState, useEffect } from 'react';
import { Button, Input, message } from 'antd';
import { useTypedSelector } from '@hooks';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { updateAssetVersionDescription } from '@api/Asset';
import { updateAssetSelectedVersion } from '@redux/actions/mediaViewerAction';
import PanelSection from '@pages/MediaViewer/components/PanelSection';
import { formatDetails } from '@pages/MediaViewer/Details/utils';
import { useResponsive } from '@hooks/useResponsive';
import {
  AssetsIcon,
  DropdownVersionsIcon,
  PlusIcon,
  SuccessIcon
} from '@icons';
import './Details.less';

type DetailsProps = {
  onClosePanel?: () => void;
  isPanelPinned?: boolean;
  onTogglePanelPin?: () => void;
};

function Details({
  onClosePanel,
  isPanelPinned,
  onTogglePanelPin
}: DetailsProps) {
  const dispatch = useTypedDispatch();
  const { isMdWidth } = useResponsive();
  const assets = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const getData = useMemo(
    () => formatDetails({ assets, selectedVersion }),
    [selectedVersion]
  );
  const isOwnerOfAsset = assets?.owner?.user?.me;
  const [detailsDescription, setDetailsDescription] = useState<string>('');
  const [isShowDescription, setIsShowDescription] = useState<boolean>(false);

  useEffect(() => {
    setDetailsDescription(selectedVersion?.description || '');
    setIsShowDescription(Boolean(selectedVersion?.description));
  }, [selectedVersion]);

  const onSaveDescription = async () => {
    if (selectedVersion?.id) {
      await updateAssetVersionDescription({
        assetVersionId: selectedVersion.id,
        description: detailsDescription
      });
      dispatch(
        updateAssetSelectedVersion({
          newSelectedVersion: {
            ...selectedVersion,
            description: detailsDescription
          }
        })
      );
      if (!detailsDescription) setIsShowDescription(false);
    }
  };

  const dropdownMenu = useMemo(
    () => [
      {
        key: 'copy-id',
        label: 'Copy media ID',
        icon: <AssetsIcon />
      },
      {
        key: 'copy-version-id',
        label: 'Copy version ID',
        icon: <DropdownVersionsIcon />
      }
    ],
    []
  );

  const onDropdownHandler = async (key: string) => {
    if (key === 'copy-id' && assets?.id) {
      await navigator.clipboard.writeText(assets.id);
      message.success({
        content: 'Media ID copied to clipboard',
        className: 'message-dark-modal',
        icon: <SuccessIcon className="anticon" />
      });
    }
    if (key === 'copy-version-id' && selectedVersion?.id) {
      await navigator.clipboard.writeText(selectedVersion.id);
      message.success({
        content: 'Version ID copied to clipboard',
        className: 'message-dark-modal',
        icon: <SuccessIcon className="anticon" />
      });
    }
  };

  const btnAddDescription =
    !isShowDescription && !detailsDescription && isOwnerOfAsset ? (
      <Button
        icon={<PlusIcon />}
        className="media_viewer_details-btn"
        type="link"
        onClick={(e) => {
          e.stopPropagation();
          setIsShowDescription(true);
        }}
      >
        Description
      </Button>
    ) : null;

  return (
    <PanelSection
      name="Details"
      extraction={btnAddDescription}
      onClose={onClosePanel}
      isPinned={isPanelPinned}
      onTogglePin={onTogglePanelPin}
      dropdownMenu={dropdownMenu}
      onDropdownHandler={onDropdownHandler}
    >
      <div className="media_viewer_inner media_viewer_details">
        {isMdWidth && btnAddDescription}
        {selectedVersion && (
          <div className="media_viewer_details-container">
            {isShowDescription && (
              <div className="media_viewer_details-block b-block-full">
                <div className="media_viewer_details-item media_viewer_details-item--auto-height">
                  <div className="media_viewer_details-label">Description</div>
                  {isOwnerOfAsset ? (
                    <Input.TextArea
                      className="media_viewer_details-input"
                      value={detailsDescription}
                      onChange={(e) => setDetailsDescription(e.target.value)}
                      onBlur={() => onSaveDescription()}
                      autoSize={{ minRows: 1 }}
                      bordered={false}
                    />
                  ) : (
                    <div className="media_viewer_details-value media_viewer_details-value--multiline">
                      {detailsDescription}
                    </div>
                  )}
                </div>
              </div>
            )}
            {getData.map((item: any, index: number) => (
              <div
                className={`media_viewer_details-block ${
                  index === 0 ? 'b-block-full' : ''
                }`}
                key={`media_viewer_details-block-${index}`}
              >
                <div className="media_viewer_details-item">
                  <div className="media_viewer_details-label">{item.label}</div>
                  <div className="media_viewer_details-value">{item.value}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PanelSection>
  );
}

export default Details;
